import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Reactoons.css';

// Символы слота - мультяшные персонажи
const SYMBOLS = [
  { id: 'purple', emoji: '👾', color: '#9B59B6', multiplier: 2 },
  { id: 'green', emoji: '👽', color: '#27AE60', multiplier: 3 },
  { id: 'orange', emoji: '🎃', color: '#E67E22', multiplier: 4 },
  { id: 'blue', emoji: '🐙', color: '#3498DB', multiplier: 5 },
  { id: 'red', emoji: '👹', color: '#E74C3C', multiplier: 6 },
  { id: 'pink', emoji: '🐷', color: '#FF69B4', multiplier: 8 },
  { id: 'yellow', emoji: '⭐', color: '#F1C40F', multiplier: 10 },
  { id: 'wild', emoji: '🃏', color: '#FFD700', multiplier: 15, isWild: true },
];

const ROWS = 3;
const COLS = 5;
const BET_OPTIONS = [0.10, 0.25, 0.50, 1.00, 2.00, 5.00, 10.00, 25.00, 50.00, 100.00];

// Линии выплат (индексы позиций)
const PAYLINES = [
  // Горизонтальные
  [[0,0], [0,1], [0,2], [0,3], [0,4]], // верхний ряд
  [[1,0], [1,1], [1,2], [1,3], [1,4]], // средний ряд
  [[2,0], [2,1], [2,2], [2,3], [2,4]], // нижний ряд
  // V-образные
  [[0,0], [1,1], [2,2], [1,3], [0,4]], // V
  [[2,0], [1,1], [0,2], [1,3], [2,4]], // ^
  // Зигзаги
  [[0,0], [0,1], [1,2], [2,3], [2,4]],
  [[2,0], [2,1], [1,2], [0,3], [0,4]],
  [[1,0], [0,1], [0,2], [0,3], [1,4]],
  [[1,0], [2,1], [2,2], [2,3], [1,4]],
  // Дополнительные
  [[0,0], [1,1], [1,2], [1,3], [0,4]],
  [[2,0], [1,1], [1,2], [1,3], [2,4]],
  [[1,0], [1,1], [0,2], [1,3], [1,4]],
  [[1,0], [1,1], [2,2], [1,3], [1,4]],
];

interface ReelSymbol {
  symbol: typeof SYMBOLS[0];
  id: string;
}

interface WinLine {
  lineIndex: number;
  positions: number[][];
  amount: number;
  symbol: typeof SYMBOLS[0];
}

interface ReactoonsProps {
  balance?: number;
  onBalanceChange?: (newBalance: number) => void;
}

const Reactoons: React.FC<ReactoonsProps> = ({
  balance: externalBalance,
  onBalanceChange
}) => {
  const [internalBalance, setInternalBalance] = useState(1000);
  const balance = externalBalance ?? internalBalance;

  const [betIndex, setBetIndex] = useState(3); // Начинаем с 1.00
  const [reels, setReels] = useState<ReelSymbol[][]>([]);
  const [spinning, setSpinning] = useState(false);
  const [win, setWin] = useState(0);
  const [winLines, setWinLines] = useState<WinLine[]>([]);
  const [showWinAnimation, setShowWinAnimation] = useState(false);
  const [autoSpin, setAutoSpin] = useState(false);
  const [spinCount, setSpinCount] = useState(0);
  const [stoppedReels, setStoppedReels] = useState<number[]>([]); // Барабаны которые остановились
  const [revealingReels, setRevealingReels] = useState<number[]>([]); // Барабаны в процессе открытия (анимация падения)
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const bet = BET_OPTIONS[betIndex];

  // Инициализация барабанов
  const initReels = useCallback(() => {
    const newReels: ReelSymbol[][] = [];
    for (let col = 0; col < COLS; col++) {
      const column: ReelSymbol[] = [];
      for (let row = 0; row < ROWS; row++) {
        const randomSymbol = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
        column.push({
          symbol: randomSymbol,
          id: `${col}-${row}-${Date.now()}-${Math.random()}`
        });
      }
      newReels.push(column);
    }
    return newReels;
  }, []);

  useEffect(() => {
    setReels(initReels());
  }, [initReels]);

  // Проверка выигрышных линий
  const checkWins = useCallback((currentReels: ReelSymbol[][]): WinLine[] => {
    const wins: WinLine[] = [];

    PAYLINES.forEach((line, lineIndex) => {
      const symbols = line.map(([row, col]) => currentReels[col]?.[row]?.symbol);
      if (symbols.some(s => !s)) return;

      let matchCount = 1;
      const firstSymbol = symbols[0];

      for (let i = 1; i < symbols.length; i++) {
        const current = symbols[i];
        if (current.id === firstSymbol.id || current.isWild || firstSymbol.isWild) {
          matchCount++;
        } else {
          break;
        }
      }

      if (matchCount >= 3) {
        const multiplier = firstSymbol.isWild ? 15 : firstSymbol.multiplier;
        const winAmount = bet * multiplier * (matchCount - 2);
        wins.push({
          lineIndex,
          positions: line.slice(0, matchCount),
          amount: winAmount,
          symbol: firstSymbol
        });
      }
    });

    return wins;
  }, [bet]);

  // Генерация одной колонки
  const generateColumn = useCallback(() => {
    const column: ReelSymbol[] = [];
    for (let row = 0; row < ROWS; row++) {
      const randomSymbol = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
      column.push({
        symbol: randomSymbol,
        id: `reel-${Date.now()}-${Math.random()}`
      });
    }
    return column;
  }, []);

  // Спин барабанов с поочерёдной остановкой
  const spin = useCallback(async () => {
    if (spinning || balance < bet) return;

    // Списываем ставку
    const newBalance = balance - bet;
    if (onBalanceChange) {
      onBalanceChange(newBalance);
    } else {
      setInternalBalance(newBalance);
    }

    setSpinning(true);
    setWin(0);
    setWinLines([]);
    setShowWinAnimation(false);
    setSpinCount(prev => prev + 1);
    setStoppedReels([]); // Сбрасываем остановленные барабаны
    setRevealingReels([]); // Сбрасываем открывающиеся барабаны

    // Генерируем финальный результат заранее
    const finalReels = initReels();

    // Анимация спина - быстрое мелькание
    const spinIntervalTime = 80;
    const spinInterval = setInterval(() => {
      setReels(prev => prev.map((column, colIndex) => {
        // Если барабан уже остановился - оставляем финальный результат
        if (stoppedReels.includes(colIndex)) {
          return finalReels[colIndex];
        }
        // Иначе генерируем случайные символы
        return generateColumn();
      }));
    }, spinIntervalTime);

    // Поочерёдно останавливаем барабаны (слева направо) с анимацией падения
    const stopDelay = 300; // Задержка между остановками барабанов
    const baseDelay = 800; // Начальная задержка перед первой остановкой
    const dropDuration = 600; // Длительность анимации падения (0.6s)

    for (let col = 0; col < COLS; col++) {
      setTimeout(() => {
        // 1. Устанавливаем финальные символы
        setReels(prev => prev.map((column, idx) =>
          idx === col ? finalReels[col] : column
        ));

        // 2. Начинаем анимацию падения (revealing)
        setRevealingReels(prev => [...prev, col]);

        // 3. После завершения анимации падения отмечаем как остановленный
        setTimeout(() => {
          setStoppedReels(prev => [...prev, col]);
        }, dropDuration);
      }, baseDelay + col * stopDelay);
    }

    // После остановки всех барабанов (учитываем время анимации падения)
    const totalSpinTime = baseDelay + COLS * stopDelay + dropDuration + 200;
    setTimeout(() => {
      clearInterval(spinInterval);
      setReels(finalReels);

      // Проверяем выигрыши
      setTimeout(() => {
        const wins = checkWins(finalReels);

        if (wins.length > 0) {
          const totalWin = wins.reduce((sum, w) => sum + w.amount, 0);
          setWin(totalWin);
          setWinLines(wins);
          setShowWinAnimation(true);

          // Добавляем выигрыш к балансу
          const winBalance = newBalance + totalWin;
          if (onBalanceChange) {
            onBalanceChange(winBalance);
          } else {
            setInternalBalance(winBalance);
          }

          // Скрываем анимацию через время
          setTimeout(() => {
            setShowWinAnimation(false);
          }, 3000);
        }

        setSpinning(false);
        setStoppedReels([]);
        setRevealingReels([]);
      }, 300);
    }, totalSpinTime);
  }, [spinning, balance, bet, initReels, generateColumn, checkWins, onBalanceChange]);

  // Авто-спин
  useEffect(() => {
    if (autoSpin && !spinning && balance >= bet) {
      const timer = setTimeout(spin, 500);
      return () => clearTimeout(timer);
    }
  }, [autoSpin, spinning, balance, bet, spin]);

  // Изменение ставки
  const changeBet = (delta: number) => {
    if (spinning) return;
    const newIndex = Math.max(0, Math.min(BET_OPTIONS.length - 1, betIndex + delta));
    setBetIndex(newIndex);
  };

  // Проверка выигрышной позиции
  const isWinningPosition = (row: number, col: number): boolean => {
    return winLines.some(line =>
      line.positions.some(([r, c]) => r === row && c === col)
    );
  };

  return (
    <div className="reactoons-container">
      {/* Фоновые частицы */}
      <div className="reactoons-particles">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          />
        ))}
      </div>

      {/* Логотип */}
      <div className="reactoons-logo">
        <h1>REACTOONS</h1>
        <div className="logo-glow"></div>
      </div>

      {/* Игровая зона */}
      <div className="reactoons-game-area">
        {/* Рамка машины */}
        <div className="slot-machine-frame">
          {/* Барабаны */}
          <div className="reels-container">
            {reels.map((column, colIndex) => {
              // Определяем состояние барабана
              const isSpinning = spinning && !revealingReels.includes(colIndex) && !stoppedReels.includes(colIndex);
              const isRevealing = revealingReels.includes(colIndex) && !stoppedReels.includes(colIndex);
              const isStopped = stoppedReels.includes(colIndex);

              return (
                <div
                  key={colIndex}
                  className={`reel ${isSpinning ? 'spinning' : ''} ${isRevealing ? 'revealing' : ''} ${isStopped ? 'stopped' : ''}`}
                >
                  {/* Overlay для затемнения неоткрытых столбцов */}
                  <div className="reel-overlay" />

                  {column.map((item, rowIndex) => (
                    <div
                      key={item.id}
                      className={`symbol ${isWinningPosition(rowIndex, colIndex) && showWinAnimation ? 'winning' : ''}`}
                      style={{
                        backgroundColor: `${item.symbol.color}33`,
                        borderColor: item.symbol.color
                      }}
                    >
                      <span className="symbol-emoji">{item.symbol.emoji}</span>
                      {isWinningPosition(rowIndex, colIndex) && showWinAnimation && (
                        <div className="win-glow" style={{ boxShadow: `0 0 30px ${item.symbol.color}` }} />
                      )}
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Оверлей спина */}
            {spinning && <div className="spin-overlay" />}
          </div>
        </div>
      </div>

      {/* Панель управления */}
      <div className="reactoons-controls">
        {/* Баланс */}
        <div className="control-panel balance-panel">
          <span className="panel-label">BALANCE</span>
          <span className="panel-value">{balance.toFixed(2)}</span>
        </div>

        {/* Выигрыш */}
        <div className={`control-panel win-panel ${showWinAnimation ? 'winner' : ''}`}>
          <span className="panel-label">WIN</span>
          <span className="panel-value win-value">{win.toFixed(2)}</span>
        </div>

        {/* Ставка */}
        <div className="control-panel bet-panel">
          <button
            className="bet-btn minus"
            onClick={() => changeBet(-1)}
            disabled={spinning || betIndex === 0}
          >
            −
          </button>
          <div className="bet-display">
            <span className="panel-label">BET</span>
            <span className="panel-value">{bet.toFixed(2)}</span>
          </div>
          <button
            className="bet-btn plus"
            onClick={() => changeBet(1)}
            disabled={spinning || betIndex === BET_OPTIONS.length - 1}
          >
            +
          </button>
        </div>
      </div>

      {/* Кнопки действий */}
      <div className="reactoons-actions">
        <button
          className={`auto-btn ${autoSpin ? 'active' : ''}`}
          onClick={() => setAutoSpin(!autoSpin)}
          disabled={spinning && !autoSpin}
        >
          {autoSpin ? 'STOP' : 'AUTO'}
        </button>

        <button
          className={`spin-btn ${spinning ? 'spinning' : ''}`}
          onClick={spin}
          disabled={spinning || balance < bet}
        >
          <span className="spin-text">{spinning ? 'SPINNING...' : 'SPIN'}</span>
          <div className="spin-glow"></div>
        </button>

        <button
          className="max-btn"
          onClick={() => setBetIndex(BET_OPTIONS.length - 1)}
          disabled={spinning}
        >
          MAX BET
        </button>
      </div>

      {/* Информация о линиях */}
      <div className="paylines-info">
        <span>🎰 {PAYLINES.length} PAYLINES</span>
        <span>💎 WILD: 🃏</span>
      </div>

      {/* Большой выигрыш */}
      {showWinAnimation && win > bet * 10 && (
        <div className="big-win-overlay">
          <div className="big-win-content">
            <h2>🎉 BIG WIN! 🎉</h2>
            <span className="big-win-amount">{win.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reactoons;
